import { useCallback } from "react";
import { StyleProp, ViewStyle } from "react-native";
import Animated from "react-native-reanimated";
import PillButton from "src/components/PillButton";
import cursor from "src/constants/cursor";
import useAnimatedOpacityStyle from "src/hooks/useAnimatedOpacityStyle";
import useDevice from "src/hooks/useDevice";
import useKeyboardListener from "src/hooks/useKeyboardListener";
import IconLeft from "src/icons/IconLeft";
import IconRight from "src/icons/IconRight";
import { paddingSmallSpacer, paddingXXL } from "src/styles/spacing";

const ImageGalleryPager = ({
	mainItemDimensions,
	mainItemContainerStyle,
	onPage,
	show = false,
	behavior,
}: {
	mainItemDimensions: {
		width: number;
		height: number;
	};
	mainItemContainerStyle: StyleProp<ViewStyle>;
	onPage: (numPages: number) => void;
	show?: boolean;
	behavior?: "FirstItem" | "LastItem" | "MiddleItem" | "None";
}) => {
	const { native, device } = useDevice();
	const showLeft = show && behavior !== "FirstItem" && behavior !== "None";
	const showRight = show && behavior !== "LastItem" && behavior !== "None";

	const pageLeft = useCallback(() => {
		onPage(-1);
	}, [onPage]);

	const pageRight = useCallback(() => {
		onPage(1);
	}, [onPage]);

	useKeyboardListener({
		ArrowLeft: pageLeft,
		ArrowRight: pageRight,
	});

	const animatedStyle = useAnimatedOpacityStyle(+show);
	const leftAnimatedStyle = useAnimatedOpacityStyle(+showLeft);
	const rightAnimatedStyle = useAnimatedOpacityStyle(+showRight);

	if (native) return null;
	const padding = device === "desktop" ? paddingSmallSpacer : paddingXXL;

	return (
		<Animated.View
			style={[
				mainItemContainerStyle,
				{
					position: "absolute",
					width: "100%",
					height: mainItemDimensions.height,
					flexDirection: "row",
					zIndex: 2,
				},
				animatedStyle,
			]}
			pointerEvents="box-none"
		>
			<Animated.View
				style={[
					{
						position: "absolute",
						alignSelf: "center",
						left: padding,
					},
					leftAnimatedStyle,
				]}
				pointerEvents="box-none"
			>
				<PillButton
					testID={showLeft ? "PageLeft" : undefined}
					disabled={!showLeft}
					Icon={IconLeft}
					onPress={pageLeft}
					containerStyle={{
						cursor: showLeft ? cursor.pointer : cursor.default,
					}}
				/>
			</Animated.View>
			<Animated.View
				style={[
					{
						position: "absolute",
						alignSelf: "center",
						right: padding,
					},
					rightAnimatedStyle,
				]}
				pointerEvents="box-none"
			>
				<PillButton
					testID={showRight ? "PageRight" : undefined}
					disabled={!showRight}
					Icon={IconRight}
					onPress={pageRight}
					containerStyle={{
						cursor: showRight ? cursor.pointer : cursor.default,
					}}
				/>
			</Animated.View>
		</Animated.View>
	);
};
export default ImageGalleryPager;
